import { useCallback, useEffect, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { useLazySearchSystemUsersQuery } from '@/app/services/lookups';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { SystemUser } from '@/rtk-query.gql';

import './CsrAutoComplete.css';

type CsrAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: SystemUser & { csr?: { fullName: string } };
};

export const CsrAutoComplete = ({
  className,
  label,
  defaultValue,
  ...autoCompleteProps
}: CsrAutoCompleteProps) => {
  const [selected, setSelected] = useState<SystemUser | undefined>(
    defaultValue,
  );
  const [inputValue, setInputValue] = useState(defaultValue?.csr?.fullName);
  const [filtered, setFiltered] = useState<SystemUser[]>([]);

  const [query] = useLazySearchSystemUsersQuery();

  useEffect(() => {
    if (defaultValue) {
      if (setInputValue) {
        setInputValue(defaultValue?.fullName as string);
      }
    }
  }, [defaultValue]);

  const search = useCallback(
    async (event: { query: string }) => {
      const { data = [] } = await query({
        search: event.query || '',
        selectionLimit: SELECTION_LIMIT,
      });

      setFiltered(data);
    },
    [query],
  );

  const handleSelect = (e: { value: SystemUser }) => {
    setSelected(e.value);
    if (setInputValue) {
      setInputValue(e.value?.fullName as string);
    }
  };

  const handleChange = (e: { value: string | SystemUser }) => {
    if (setInputValue) {
      const value =
        typeof e.value === 'object' && e.value !== null
          ? e.value.fullName
          : (e.value as string);
      setInputValue(value as string);
    }
  };

  return (
    <div
      className={`[ csr-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo="self"
        suggestions={filtered}
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
      />
    </div>
  );
};
